import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import OverlayDialog from './OverlayDialog';
import Button from './Button';
import ButtonOutline from './ButtonOutline';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { COLORS } from '../constants/styles';
import { RegionHelper } from '../../../utils/RegionHelper';
import { startChallenge, destroyChallenge, RenderChallenge } from '../../../utils/challengerMain';

type Variant = 'EU' | 'AP';

// EU-specific styles (original from privacyCenter/socialAccounts)
const ModalContainerEU = styled.div`
    line-height: 1.5;
    padding: 30px 0 10px 0;
`;

const ConfirmationContentEU = styled.div`
    color: ${COLORS.dark};
    margin-bottom: 2rem;

    p {
        margin: 0 0 1rem 0;
        font-weight: 400;
        font-style: normal;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0px;
    }
`;

const ActionsEU = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;

    & > button:first-child {
        border-color: ${COLORS.rakutenRed};
        color: ${COLORS.rakutenRed};
        margin: 0;
    }

    & > button {
        max-width: 45%;
        width: 200px;
    }
`;

// AP-specific styles (original from socialAccounts/components)
const ModalContentAP = styled.div`
    padding: 30px 0 10px 0;
`;

const MessageAP = styled.p`
    color: ${COLORS.dark};
    font-size: 16px;
    line-height: 24px;
    margin: 0 0 2rem 0;
`;

const ActionsAP = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 40px;

    & > button:first-child {
        border-color: ${COLORS.rakutenRed};
        color: ${COLORS.rakutenRed};
        margin: 0;
    }

    & > button {
        max-width: 45%;
        width: 200px;
    }
`;

export interface UnlinkConfirmationModalProps {
    isOpen: boolean;
    providerName: string;
    serviceName: string | JSX.Element;
    onClose: () => void;
    onConfirm: () => void;
    unlinking: boolean;
    challengerPid?: string;
}

const UnlinkConfirmationModal: FC<UnlinkConfirmationModalProps> = ({
    isOpen,
    providerName,
    serviceName,
    onClose,
    onConfirm,
    unlinking,
    challengerPid,
}) => {
    useEffect(() => {
        if (challengerPid) {
            startChallenge(challengerPid);
            return () => {
                destroyChallenge(challengerPid);
            };
        }
    }, [challengerPid]);

    if (!isOpen) {
        return null;
    }

    // Automatically detect region variant (fallback to EU if unable to determine)
    let variant: Variant = 'EU';
    try {
        variant = RegionHelper.isAP() ? 'AP' : 'EU';
    } catch (error) {
        // Fallback to EU if RegionHelper is unavailable (e.g., in tests)
        variant = 'EU';
    }

    const modalTitle = {
        ...TranslationHelpers.getMessageDescriptor('social.unlink.confirmation.title'),
        values: { provider: providerName },
    };

    // EU variant - 100% original
    if (variant === 'EU') {
        return (
            <OverlayDialog title={modalTitle} isOpen={isOpen} onClose={onClose}>
                <ModalContainerEU data-qa-id="social-unlink-confirmation">
                    <ConfirmationContentEU>
                        <p>
                            {TranslationHelpers.getFormattedMessage(
                                'social.unlink.confirmation.description1',
                                {
                                    provider: providerName,
                                    serviceName: serviceName,
                                },
                            )}
                        </p>
                        <p>
                            {TranslationHelpers.getFormattedMessage(
                                'social.unlink.confirmation.description2',
                            )}
                        </p>
                    </ConfirmationContentEU>
                    <ActionsEU>
                        <ButtonOutline
                            data-qa-id="unlink-confirm"
                            id="confirm"
                            type="button"
                            onClick={onConfirm}
                            loading={unlinking}
                            disabled={unlinking}
                            text={TranslationHelpers.getMessageDescriptor(
                                'social.unlink.confirm.button',
                            )}
                        />
                        <Button
                            qaId="unlink-cancel"
                            id="cancel"
                            primary
                            type="button"
                            text={TranslationHelpers.getMessageDescriptor('button.cancel')}
                            disabled={unlinking}
                            onClick={onClose}
                            success={false}
                        />
                    </ActionsEU>
                </ModalContainerEU>
            </OverlayDialog>
        );
    }

    // AP variant - with bold serviceName
    return (
        <OverlayDialog title={modalTitle} isOpen={isOpen} onClose={onClose}>
            <ModalContentAP data-qa-id="unlink-confirmation-modal">
                <MessageAP>
                    {TranslationHelpers.getFormattedMessage(
                        'social.unlink.confirmation.description1',
                        {
                            provider: providerName,
                            serviceName: <strong>{serviceName}</strong>,
                        },
                    )}
                    <br />
                    <br />
                    {TranslationHelpers.getFormattedMessage(
                        'social.unlink.confirmation.description2',
                    )}
                </MessageAP>
                {challengerPid && <RenderChallenge cpid={challengerPid} />}
                <ActionsAP>
                    <ButtonOutline
                        data-qa-id="unlink-confirm-button"
                        type="button"
                        onClick={onConfirm}
                        loading={unlinking}
                        disabled={unlinking}
                        text={{ id: 'social.accounts.unlink', defaultMessage: 'Unlink' }}
                        id={''}
                    />
                    <Button
                        qaId="unlink-cancel-button"
                        type="button"
                        primary
                        text={{ id: 'button.cancel', defaultMessage: 'Cancel' }}
                        disabled={unlinking}
                        onClick={onClose}
                        success={false}
                    />
                </ActionsAP>
            </ModalContentAP>
        </OverlayDialog>
    );
};

export default UnlinkConfirmationModal;
